import React, { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../ui/card";
import { Button } from "../../../ui/button";
import { Input } from "../../../ui/input";
import { Label } from "../../../ui/label";
import { Textarea } from "../../../ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../ui/select";
import { toast } from "sonner";
import { ArrowLeft, Save, Plus, X, MapPin, Phone, Mail, Clock, Image as ImageIcon } from "lucide-react";
import AdminNavbar from "../../../AdminNavbar";
import { clinicService } from "../../../../services";
import { useDataFetching } from "../../../../hooks";

const AdminClinicEdit = () => {
  const navigate = useNavigate();
  const { clinicId } = useParams();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    pincode: "",
    description: "",
    specializations: [],
    facilities: [],
    workingHours: {
      monday: { open: "09:00", close: "17:00", closed: false },
      tuesday: { open: "09:00", close: "17:00", closed: false },
      wednesday: { open: "09:00", close: "17:00", closed: false },
      thursday: { open: "09:00", close: "17:00", closed: false },
      friday: { open: "09:00", close: "17:00", closed: false },
      saturday: { open: "09:00", close: "15:00", closed: false },
      sunday: { open: "09:00", close: "15:00", closed: true },
    },
    rating: "",
    status: "active",
  });

  const [newSpecialization, setNewSpecialization] = useState("");
  const [newFacility, setNewFacility] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [clinicImage, setClinicImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  // Use custom hook for data fetching
  const { data: clinic, isLoading: dataLoading } = useDataFetching(
    () => clinicService.getClinicById(clinicId),
    null,
    { errorMessage: 'Failed to load clinic details.' }
  );

  // Update form data when clinic data is loaded
  useEffect(() => {
    if (clinic) {
      setFormData({
        name: clinic.name || "",
        email: clinic.email || "",
        phone: clinic.phone || "",
        address: clinic.address || "",
        city: clinic.city || "",
        state: clinic.state || "",
        pincode: clinic.pincode || "",
        description: clinic.description || "",
        specializations: clinic.specializations || [],
        facilities: clinic.facilities || [],
        workingHours: clinic.workingHours || {
          monday: { open: "09:00", close: "17:00", closed: false },
          tuesday: { open: "09:00", close: "17:00", closed: false },
          wednesday: { open: "09:00", close: "17:00", closed: false },
          thursday: { open: "09:00", close: "17:00", closed: false },
          friday: { open: "09:00", close: "17:00", closed: false },
          saturday: { open: "09:00", close: "15:00", closed: false },
          sunday: { open: "09:00", close: "15:00", closed: true },
        },
        rating: clinic.rating || "",
        status: clinic.status || "active",
      });
      
      // Set existing image preview if available
      if (clinic.imageUrl) {
        setImagePreview(clinic.imageUrl);
      }
      
      setIsLoading(false);
    }
  }, [clinic]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleWorkingHoursChange = (day, field, value) => {
    setFormData(prev => ({
      ...prev,
      workingHours: {
        ...prev.workingHours,
        [day]: {
          ...prev.workingHours[day],
          [field]: value
        }
      }
    }));
  };

  const handleArrayField = (field, action, value = null, index = null) => {
    setFormData(prev => {
      const currentArray = prev[field] || [];
      let newArray;

      switch (action) {
        case 'add':
          newArray = [...currentArray, value];
          break;
        case 'remove':
          newArray = currentArray.filter((_, i) => i !== index);
          break;
        default:
          newArray = currentArray;
      }

      return {
        ...prev,
        [field]: newArray
      };
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error("Please select a valid image file");
        return;
      }
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image size should be less than 5MB");
        return;
      }
      setClinicImage(file);
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setClinicImage(null);
    // Reset to original image if it exists
    if (clinic?.imageUrl) {
      setImagePreview(clinic.imageUrl);
    } else {
      setImagePreview(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    
    try {
      // Update clinic via API (with image if provided)
      await clinicService.updateClinic(clinicId, formData, clinicImage || null);
      
      toast.success("Clinic updated successfully!");
      navigate(`/admin/clinics/${clinicId}`);
    } catch (error) {
      console.error("Error updating clinic:", error);
      toast.error(error.message || "Failed to update clinic. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };


  const days = [
    { key: "monday", label: "Monday" },
    { key: "tuesday", label: "Tuesday" },
    { key: "wednesday", label: "Wednesday" },
    { key: "thursday", label: "Thursday" },
    { key: "friday", label: "Friday" },
    { key: "saturday", label: "Saturday" },
    { key: "sunday", label: "Sunday" },
  ];

  if (dataLoading || isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AdminNavbar />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading clinic details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!clinic) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AdminNavbar />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Clinic Not Found</h1>
            <p className="text-gray-600 mb-6">The clinic you're trying to edit doesn't exist or has been removed.</p>
            <Link to="/admin/clinics">
              <Button>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Clinics
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminNavbar />
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <div className="flex items-center gap-4">
            <Link to={`/admin/clinics/${clinicId}`}>
              <Button variant="outline" size="sm">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Clinic
              </Button>
            </Link>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Edit Clinic</h1>
              <p className="text-gray-600 mt-2">Update clinic information and settings</p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Information */}
            <div className="lg:col-span-2 space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Basic Information</CardTitle>
                  <CardDescription>
                    Essential details about the clinic
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Clinic Name *</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => handleInputChange("name", e.target.value)}
                        placeholder="City Dental Clinic"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleInputChange("email", e.target.value)}
                        placeholder="info@citydental.com"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone Number *</Label>
                      <Input
                        id="phone"
                        value={formData.phone}
                        onChange={(e) => handleInputChange("phone", e.target.value)}
                        placeholder="+91 98765 43210"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="city">City *</Label>
                      <Input
                        id="city"
                        value={formData.city}
                        onChange={(e) => handleInputChange("city", e.target.value)}
                        placeholder="Mumbai"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="state">State *</Label>
                      <Input
                        id="state"
                        value={formData.state}
                        onChange={(e) => handleInputChange("state", e.target.value)}
                        placeholder="Maharashtra"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="pincode">ZIP Code *</Label>
                      <Input
                        id="pincode"
                        value={formData.pincode}
                        onChange={(e) => handleInputChange("pincode", e.target.value)}
                        placeholder="400001"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="rating">Rating *</Label>
                      <Input
                        id="rating"
                        type="number"
                        value={formData.rating}
                        onChange={(e) => handleInputChange("rating", e.target.value)}
                        placeholder="4.5"
                        min="0"
                        max="5"
                        step="0.1"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="status">Status *</Label>
                      <Select
                        value={formData.status}
                        onValueChange={(value) => handleInputChange("status", value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="inactive">Inactive</SelectItem>
                          <SelectItem value="pending">Pending</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="address">Complete Address *</Label>
                    <Textarea
                      id="address"
                      value={formData.address}
                      onChange={(e) => handleInputChange("address", e.target.value)}
                      placeholder="123 Main Street, Downtown Area"
                      rows={3}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => handleInputChange("description", e.target.value)}
                      placeholder="Brief description about the clinic, services offered, and approach to patient care"
                      rows={4}
                    />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Specializations & Facilities</CardTitle>
                  <CardDescription>
                    Medical specializations and available facilities
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-2">
                    <Label>Specializations</Label>
                    <div className="space-y-2">
                      {formData.specializations.map((spec, index) => (
                        <div key={index} className="flex items-center gap-2">
                          <Input value={spec} readOnly />
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => handleArrayField("specializations", "remove", null, index)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                      <div className="flex gap-2">
                        <Input
                          value={newSpecialization}
                          onChange={(e) => setNewSpecialization(e.target.value)}
                          placeholder="Add specialization"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => {
                            if (newSpecialization.trim()) {
                              handleArrayField("specializations", "add", newSpecialization.trim());
                              setNewSpecialization("");
                            }
                          }}
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Facilities</Label>
                    <div className="space-y-2">
                      {formData.facilities.map((facility, index) => (
                        <div key={index} className="flex items-center gap-2">
                          <Input value={facility} readOnly />
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => handleArrayField("facilities", "remove", null, index)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                      <div className="flex gap-2">
                        <Input
                          value={newFacility}
                          onChange={(e) => setNewFacility(e.target.value)}
                          placeholder="Add facility"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => {
                            if (newFacility.trim()) {
                              handleArrayField("facilities", "add", newFacility.trim());
                              setNewFacility("");
                            }
                          }}
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Working Hours</CardTitle>
                  <CardDescription>
                    Set the clinic's operating hours for each day
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {days.map((day) => (
                      <div key={day.key} className="flex items-center gap-4 p-4 border rounded-lg">
                        <div className="w-24">
                          <Label className="font-medium">{day.label}</Label>
                        </div>
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id={`closed-${day.key}`}
                            checked={formData.workingHours[day.key].closed}
                            onChange={(e) => handleWorkingHoursChange(day.key, "closed", e.target.checked)}
                            className="rounded"
                          />
                          <Label htmlFor={`closed-${day.key}`} className="text-sm">Closed</Label>
                        </div>
                        {!formData.workingHours[day.key].closed && (
                          <>
                            <div className="flex items-center gap-2">
                              <Label className="text-sm">Open:</Label>
                              <Input
                                type="time"
                                value={formData.workingHours[day.key].open}
                                onChange={(e) => handleWorkingHoursChange(day.key, "open", e.target.value)}
                                className="w-32"
                              />
                            </div>
                            <div className="flex items-center gap-2">
                              <Label className="text-sm">Close:</Label>
                              <Input
                                type="time"
                                value={formData.workingHours[day.key].close}
                                onChange={(e) => handleWorkingHoursChange(day.key, "close", e.target.value)}
                                className="w-32"
                              />
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Clinic Image</CardTitle>
                  <CardDescription>
                    Upload an image for the clinic
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {imagePreview ? (
                    <div className="space-y-2">
                      <div className="relative w-full aspect-square rounded-lg overflow-hidden border-2 border-gray-200">
                        <img
                          src={imagePreview}
                          alt="Clinic preview"
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={removeImage}
                        className="w-full"
                      >
                        <X className="h-4 w-4 mr-2" />
                        Remove Image
                      </Button>
                    </div>
                  ) : (
                    <label className="flex flex-col items-center justify-center w-full h-48 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors">
                      <div className="flex flex-col items-center justify-center pt-5 pb-6">
                        <ImageIcon className="w-10 h-10 mb-3 text-gray-400" />
                        <p className="mb-2 text-sm text-gray-500">
                          <span className="font-semibold">Click to upload</span> or drag and drop
                        </p>
                        <p className="text-xs text-gray-500">PNG, JPG, GIF up to 5MB</p>
                      </div>
                      <input
                        type="file"
                        className="hidden"
                        accept="image/*"
                        onChange={handleImageChange}
                      />
                    </label>
                  )}
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle>Actions</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Button type="submit" className="w-full" disabled={isSaving}>
                    <Save className="h-4 w-4 mr-2" />
                    {isSaving ? 'Saving...' : 'Save Changes'}
                  </Button>
                  <Link to={`/admin/clinics/${clinicId}`}>
                    <Button variant="outline" className="w-full">
                      Cancel
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminClinicEdit;
